import { useState, useEffect } from "react";
import { api, type AuthUser } from "@/lib/api";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  editionId: string;
  serviceType?: "course" | "diplomado";
  onSuccess: () => void;
}

export default function ManualEnrollModal({ isOpen, onClose, editionId, serviceType = "course", onSuccess }: Props) {
  const [students, setStudents] = useState<AuthUser[]>([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [courtesy, setCourtesy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      api.getUsers()
        .then(users => setStudents(users.filter(u => u.role === "estudiante")))
        .catch(() => setStudents([]));
      setSelectedStudent("");
      setCourtesy(false);
      setError("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedStudent) {
      setError("Debes seleccionar un estudiante");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await api.adminEnrollment({
        student_id: selectedStudent,
        ...(serviceType === "diplomado" ? { diplomado_edition_id: editionId } : { edition_id: editionId }),
        courtesy,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || "Error al inscribir estudiante");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#0F172A]/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="card w-full max-w-md relative animate-[slideUp_0.3s_ease-out]">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-[var(--color-text-primary)]">Inscribir Estudiante</h2>
          <button onClick={onClose} className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer">
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-[var(--color-text-secondary)] mb-2">
              Seleccionar estudiante registrado
            </label>
            <select
              className="input-field"
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              required
            >
              <option value="">-- Selecciona un estudiante --</option>
              {students.map(s => (
                <option key={s.id} value={s.id}>{s.full_name} ({s.email})</option>
              ))}
            </select>
          </div>

          <label className="flex items-start gap-3 p-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-tertiary)] cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={courtesy}
              onChange={(e) => setCourtesy(e.target.checked)}
            />
            <span>
              <span className="block text-sm font-semibold text-[var(--color-text-primary)]">Cortesía / Exonerado</span>
              <span className="block text-xs text-[var(--color-text-muted)] mt-1 leading-relaxed">
                El estudiante queda liquidado para certificados, pero este monto <strong>no cuenta</strong> como ingreso real (no llegó dinero).
              </span>
            </span>
          </label>
          
          <div className="bg-[var(--color-bg-tertiary)] p-3 rounded-xl border border-[var(--color-border)]">
            <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
              <i className="ri-information-line text-[var(--color-accent)] mr-1"></i>
              {courtesy
                ? 'La inscripción se marcará como "Aceptada" con pago "Cortesía / Exonerado". No suma a las ganancias del dashboard.'
                : 'La inscripción se marcará como "Aceptada" y el pago quedará como "Pendiente". Se enviará correo de confirmación.'}
            </p>
          </div>

          {error && <p className="text-sm text-red-400 bg-red-400/10 p-2 rounded">{error}</p>}
          
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !selectedStudent}
              className="btn-primary flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {loading ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className={courtesy ? "ri-gift-line" : "ri-user-add-line"}></i>
              )}
              {loading ? "Inscribiendo..." : courtesy ? "Inscribir con cortesía" : "Inscribir Estudiante"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
